import { z } from 'zod';

/** Identifiants : UUID générés par la base. */
export const Id = z.uuid();
export type Id = z.infer<typeof Id>;

/** Horodatage ISO 8601 en UTC, toujours produit par le serveur. */
export const Timestamp = z.iso.datetime({ offset: true });

/** Montant décimal exact, en chaîne (« 1234.50 »). Jamais de nombre flottant. */
/** Positif ou nul : aucun montant négatif accepté en entrée (décision N9 du 01/10/2026). */
export const Money = z.string().regex(/^\d{1,10}(\.\d{1,2})?$/, 'Montant invalide (positif ou nul, 2 décimales au plus)');
/** Quantité décimale (3 décimales max). */
export const Quantity = z.string().regex(/^\d{1,7}(\.\d{1,3})?$/, 'Quantité invalide');
/** Taux de TVA en pourcentage (« 20.00 »). */
export const VatRate = z.string().regex(/^\d{1,2}(\.\d{1,2})?$/, 'Taux de TVA invalide');

export const Email = z.email().max(254).transform((v) => v.trim().toLowerCase());
/** Téléphone libre (coordonnées du garage, affichées telles quelles). */
export const Phone = z.string().trim().min(6).max(30);
/**
 * Téléphone d'un client au format international E.164 (vague 4) : espaces, points, tirets et
 * parenthèses retirés, puis « +<indicatif><numéro> » exigé. Saisie nationale : `toE164` (`phone.ts`).
 */
export const PhoneE164 = z
  .string()
  .transform((v) => v.replace(/[\s.\-()]/g, ''))
  .pipe(z.string().regex(/^\+[1-9]\d{6,14}$/, 'Numéro au format international attendu (ex. +33 6 12 34 56 78)'));
/** Pays (ISO 3166-1 alpha-2, majuscules). */
export const CountryCode = z
  .string()
  .trim()
  .transform((v) => v.toUpperCase())
  .pipe(z.string().regex(/^[A-Z]{2}$/, 'Code pays à deux lettres attendu (ex. FR)'));
export const HexColor = z.string().regex(/^#[0-9a-fA-F]{6}$/, 'Couleur hexadécimale attendue (#RRGGBB)');
/**
 * Vague 6 : présence de la marque du garage dans l'interface (passe de design à venir) : `discreet`
 * (défaut), `affirmed`, `immersive`. Le portail suit le mode du garage.
 */
export const BrandMode = z.enum(['discreet', 'affirmed', 'immersive']);
export type BrandMode = z.infer<typeof BrandMode>;
export const Password = z.string().min(10, '10 caractères minimum').max(200);
export const ShortText = z.string().trim().min(1).max(200);
export const LongText = z.string().trim().max(5000);

/**
 * Registre des champs « montant ». Les sérialiseurs de l'API retirent ces champs de toute réponse
 * destinée à un utilisateur sans `amounts.view`. Un champ montant est donc toujours facultatif en sortie.
 */
export const amountRegistry = z.registry<{ amount: true }>();

/** Champ montant de sortie : retiré côté serveur sans `amounts.view`, `null` si non renseigné. */
export function amount() {
  const schema = Money.nullable().optional();
  schema.register(amountRegistry, { amount: true });
  return schema;
}

export function isAmountSchema(schema: z.ZodType): boolean {
  return amountRegistry.has(schema);
}

// ─── Énumérations ────────────────────────────────────────────────────────────

export const Role = z.enum(['owner', 'partner', 'frontdesk', 'mechanic', 'readonly']);
export type Role = z.infer<typeof Role>;

export const InterventionStatus = z.enum([
  'received',
  'in_progress',
  'awaiting_decision',
  'awaiting_parts',
  'ready',
  'closed',
]);
export type InterventionStatus = z.infer<typeof InterventionStatus>;

export const StorageDayType = z.enum(['calendar', 'business']);
export const StorageStartRule = z.enum(['ready_clicked']);
export const TaxMode = z.enum(['ttc', 'ht']);
export const InvoicingDraftKind = z.enum(['quote', 'invoice']);

/** `customer` : photo jointe par le client à une question (vague 4), jamais une photo du garage. */
export const MediaKind = z.enum(['entry_condition', 'report', 'customer']);
/** Types qu'un membre du garage peut téléverser. */
export const GarageMediaKind = z.enum(['entry_condition', 'report']);
/** Auteur d'une photo : le garage, ou le client via le portail (vague 4). */
export const MediaUploader = z.enum(['garage', 'customer']);
export type MediaUploader = z.infer<typeof MediaUploader>;
export const MediaVisibility = z.enum(['internal', 'published']);
export const MediaVariant = z.enum(['thumb', 'display', 'original']);

export const LineKind = z.enum(['part', 'labor']);
export const ComplementStatus = z.enum([
  'draft',
  'pending_validation',
  'sent',
  'accepted',
  'refused',
  'postponed',
  'superseded',
  'cancelled',
  // Vague 6 : le client a demandé une modification de la version envoyée (jamais un accord).
  'change_requested',
]);
/**
 * Réponse du client à une version envoyée. Vague 6 : `change_requested` (demande de modification, avec un
 * commentaire obligatoire) : **jamais un accord** ; le garage révise (nouvelle version) ou annule.
 */
export const DecisionValue = z.enum(['accepted', 'refused', 'postponed', 'change_requested']);
export type DecisionValue = z.infer<typeof DecisionValue>;
/** Seule réponse qui vaut accord du client (invariant : un silence, un message ou une demande de modification n'en sont jamais). */
export const AGREEMENT_DECISIONS: readonly DecisionValue[] = ['accepted'];
/** Réponses définitives pour une version (une seule par version, index unique) ; `postponed` ne l'est pas. */
export const FINAL_DECISIONS: readonly DecisionValue[] = ['accepted', 'refused', 'change_requested'];
/** Canal de la décision : portail (client) ou saisie par le garage (téléphone, sur place). */
export const DecisionChannel = z.enum(['portal', 'phone', 'in_person']);
export const GarageDecisionChannel = z.enum(['phone', 'in_person']);

/** Vague 6 : `system` = événement du dossier dans la conversation (aucun texte libre, rendu à la lecture). */
export const MessageAuthorType = z.enum(['customer', 'garage', 'system']);
/** Vague 6 : message écrit (`text`) ou événement du dossier (`event`). */
export const MessageKind = z.enum(['text', 'event']);
export const MediaLinkEntity = z.enum(['complement', 'work_item', 'message']);
export const ActorType = z.enum(['user', 'portal', 'platform', 'system']);
export const EmailDeliveryStatus = z.enum(['queued', 'sent', 'failed', 'cancelled']);

/**
 * Tableau en query string (vague 4) : clé répétée `?make=Porsche&make=Alpine` (le client typé
 * `api()` sérialise ainsi les tableaux). Une seule occurrence arrive en chaîne : elle devient un tableau.
 */
export const queryList = <T extends z.ZodType>(item: T, max: number) =>
  z.preprocess((v) => (typeof v === 'string' ? [v] : v), z.array(item).max(max));

export const IdParams = z.strictObject({ id: Id });
export const Empty = z.strictObject({});
export const Ok = z.object({ ok: z.literal(true) });
